import type React from 'react';
import { useEffect, useState } from 'react';
import { classNames } from 'tradera/utils/class-names';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'tradera/state/hooks';
import { useTranslator } from 'tradera/lang/translate';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import { Button } from 'tradera/components/atoms/button/button';
import { selectSaveSearchData } from 'tradera/state/discover/selectors';
import { useSaveSearchMutation } from 'tradera/state/services/touchweb-search-api';
import {
    useRemoveSavedSearchesMutation,
    useToggleEmailNotificationMutation
} from 'tradera/apps/my-tradera/saved-searches/saved-searches-api';
import {
    EmailNotificationToolTip,
    EmailNotificationTooltipPosition
} from './email-notification-tooltip';
import { showSuccessToast, showErrorToast } from 'tradera/services/toasts';
import useIsLoggedIn from 'tradera/hooks/use-is-loggedin';
import { toSearchString } from 'tradera/utils/url';
import { signIn } from 'tradera/state/ui/actions';
import BookmarkFillIcon from '@tradera/blueprint/theme/icons2/bookmark-fill.svg?jsx';
import BookmarkIcon from '@tradera/blueprint/theme/icons2/bookmark.svg?jsx';
import { showSaveSearchHint, hideSaveSearchHint } from './utils';
import {
    removeSavedSearch as removeSavedSearchFromStorage,
    storeSavedSearch
} from 'tradera/state/services/helpers/saved-searches-storage';
import * as searchGtm from 'tradera/apps/tradera/components/discover/helpers/search-gtm';
import styles from './save-search-button.module.scss';
import { Popover } from 'tradera/components/popover/popover';
import { openSearchForm } from 'tradera/state/ui/reducer';
import { GtmService } from 'tradera/services/google-tagmanager/google-tagmanager-service';

export type ButtonType = 'default' | 'icon' | 'no-icon';

export type SaveSearchButtonProps = React.PropsWithChildren<{
    type?: ButtonType;
    successToast?: boolean;
    showHint?: boolean;
    saveText?: string;
    emailDefaultOff?: boolean;
    onSaveClick?: (event?: React.MouseEvent<HTMLElement>) => void;
    gaActionString?: string;
    id?: string;
    isVertical?: boolean;
    containerClassName?: string;
    iconColor?: 'default' | 'white';
    emailTooltipPosition?: EmailNotificationTooltipPosition;
}>;

export const SaveSearchButton = ({
    type = 'default',
    successToast = false,
    showHint = true,
    saveText,
    emailDefaultOff = false,
    onSaveClick,
    gaActionString = searchGtm.GA_EVENT_SAVE_SEARCH,
    id = 'save-search-button',
    isVertical = false,
    containerClassName = '',
    iconColor = 'default',
    emailTooltipPosition = EmailNotificationTooltipPosition.Bottom
}: SaveSearchButtonProps) => {
    const { t } = useTranslator();
    const dispatch = useAppDispatch();
    const [isSaved, setIsSaved] = useState(false);
    const [savedSearchId, setSavedSearchId] = useState<number>(0);
    const [showEmailTooltip, setShowEmailTooltip] = useState(false);
    const [showHintTooltip, setShowHintTooltip] = useState(false);
    const isLoggedIn = useIsLoggedIn();
    const isMobile = useIsMobile();
    const saveSearchData = useSelector(selectSaveSearchData);
    const {
        searchName,
        categoryId,
        queryParams,
        isSavedSearchEmailEnabled,
        totalItems
    } = saveSearchData;

    useEffect(() => {
        const shouldShowHint = showHint && showSaveSearchHint();
        setShowHintTooltip(shouldShowHint);
        if (shouldShowHint) {
            dispatch(openSearchForm());
        }
    }, [showHint, dispatch]);

    const [saveSearch, saveSearchResponse] = useSaveSearchMutation({
        fixedCacheKey: 'shared-save-search'
    });
    const { status: saveStatus, data: saveSearchResponseData } =
        saveSearchResponse;

    const isSavePending = saveStatus === 'pending';
    const [
        removeSavedSearches,
        { isLoading: isRemovePending, data: removeSearchResponseData }
    ] = useRemoveSavedSearchesMutation({
        fixedCacheKey: 'shared-remove-search'
    });

    const hasPendingRequest = isSavePending || isRemovePending;
    const [toggleEmailNotification] = useToggleEmailNotificationMutation();

    const hideHint = () => {
        hideSaveSearchHint();
        setShowHintTooltip(false);
    };

    const handleLogin = () => {
        const query = {
            ...queryParams,
            saveSearch: 'true'
        };
        const redirectUrl = `${window.location.pathname}${toSearchString(
            query
        )}`;
        dispatch(signIn({ redirectUrl }));
    };

    useEffect(() => {
        setIsSaved(false);
        setSavedSearchId(0);
        setShowEmailTooltip(false);
    }, [saveSearchData]);

    useEffect(() => {
        if (isSavePending) {
            setIsSaved(true);
        }

        if (saveSearchResponseData?.id) {
            setSavedSearchId(saveSearchResponseData.id);
        }
    }, [saveSearchResponseData, isSavePending]);

    useEffect(() => {
        if (isRemovePending) {
            setIsSaved(false);
        }

        if (!removeSearchResponseData) {
            setSavedSearchId(0);
        }
    }, [removeSearchResponseData, isRemovePending]);

    const handleSaveSearch = () => {
        if (!isLoggedIn) {
            return handleLogin();
        }
        if (onSaveClick) {
            onSaveClick();
        }
        hideHint();
        saveSearch({
            savedQueryName: searchName,
            categoryId,
            ...queryParams
        })
            .unwrap()
            .then(({ id }) => {
                storeSavedSearch(id, totalItems);
                if (successToast) {
                    showSuccessToast('searchResults_saveSearch_success');
                } else {
                    setShowEmailTooltip(true);
                }
                if (emailDefaultOff) {
                    toggleEmailNotification({
                        //ToggleEmailNotification should NOT set sendPush but it's currently required in the contract.
                        //The WebApi SavedSearchEmailNotificationApiRequest should be split into two individual requests where the current one (with both email and push) is used from the apps and a new one (email only) is used from web.
                        savedSearchDto: {
                            id,
                            sendMail: false,
                            sendPush: false
                        }
                    });
                }
                GtmService.trackGA4('search_saved', {
                    saved_search_type: gaActionString,
                    saved_search_id: id
                });
                return;
            })
            .catch(() => {
                setShowEmailTooltip(false);
                setIsSaved(false);
                showErrorToast('searchResults_saveSearch_error');
            });
        searchGtm.trackAction(gaActionString, 'Saved');
    };

    const handleRemoveSavedSearch = () => {
        if (!savedSearchId) {
            return;
        }
        setShowEmailTooltip(false);
        removeSavedSearches({ ids: [savedSearchId] }).catch(() => {
            setIsSaved(true);
            showErrorToast('searchResults_saveSearch_error');
        });
        removeSavedSearchFromStorage(savedSearchId);
        GtmService.trackGA4('saved_search_deleted', {
            saved_search_id: savedSearchId
        });
        searchGtm.trackAction(gaActionString, 'Removed');
    };

    const handleClick = () => {
        if (hasPendingRequest) {
            return;
        }
        if (savedSearchId) {
            handleRemoveSavedSearch();
            return;
        }
        handleSaveSearch();
    };

    const buttonTheme = type === 'icon' ? 'text' : 'primary';
    const hideText = type === 'icon';
    const hideIcon = type === 'no-icon';

    const iconClassName = classNames('icon icon-md', {
        'icon-white': iconColor === 'white'
    });
    const buttonClass = classNames({
        [styles.verticalIcon]: isVertical
    });
    const containerClass = classNames(
        'tooltip_container tooltip-toggle tooltip-toggle-show position-relative',
        {
            [containerClassName]: containerClassName
        }
    );
    const centerTooltip = type === 'default' && isMobile;

    const tooltipPosition = isVertical
        ? EmailNotificationTooltipPosition.Left
        : emailTooltipPosition;

    return (
        <div className={containerClass} id={id}>
            <Popover
                title={t('searchResults_saveSearch_tooltip_title')}
                text={t('searchResults_saveSearch_tooltip')}
                buttonText={t('searchResults_saveSearch_save')}
                onClick={handleSaveSearch}
                onClose={hideHint}
                defaultOpen={showHintTooltip && showHint}>
                <Button
                    theme={buttonTheme}
                    variant={isSaved ? 'outline' : 'filled'}
                    fluid
                    size={isMobile && isVertical ? 'xs' : 'sm'}
                    onClick={handleClick}
                    className={buttonClass}
                    data-test-save-search-button
                    data-test-save-search-button-is-saved={
                        hasPendingRequest ? null : isSaved
                    }
                    icon={
                        hideIcon || hideText ? null : isSaved ? (
                            <BookmarkFillIcon />
                        ) : (
                            <BookmarkIcon />
                        )
                    }>
                    {isSaved ? (
                        hideText ? (
                            <BookmarkFillIcon className={iconClassName} />
                        ) : (
                            t('searchResults_saveSearch_saved')
                        )
                    ) : (
                        <>
                            {hideText ? (
                                <BookmarkIcon className={iconClassName} />
                            ) : saveText ? (
                                saveText
                            ) : (
                                t('searchResults_saveSearch_save')
                            )}
                        </>
                    )}
                </Button>
            </Popover>
            {showEmailTooltip && (
                <EmailNotificationToolTip
                    searchName={searchName}
                    searchId={savedSearchId}
                    isSavedSearchEmailEnabled={isSavedSearchEmailEnabled}
                    onClose={() => setShowEmailTooltip(false)}
                    center={centerTooltip}
                    position={tooltipPosition}
                />
            )}
        </div>
    );
};
